import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/**
 * EditableInput
 *
 * Props:
 * - value: initial value (string/date)
 * - mode: "text" | "date" | "status"
 * - placeholder: optional placeholder text
 * - onSave: (newVal, mode) => Promise<void> or void
 * - detectDate?: optional fn to check if a string is a date
 * - formatDate?: optional fn to format date
 */
export default function EditableInput({
  value,
  mode = "text",
  placeholder = "",
  onSave,
  readOnly,
  detectDate,
  formatDate,
}) {
  // ---------- Helpers ----------
  const defaultFormatDate = (date) => {
    if (!date) return "";
    const d = date.getDate().toString().padStart(2, "0");
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const parseDate = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}`);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const isDateString =
    detectDate ||
    ((v) => {
      if (!v) return false;
      if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(String(v))) return true;
      if (/^\d{4}-\d{2}-\d{2}T?/.test(String(v))) return true;
      return parseDate(v) !== null;
    });

  const format = formatDate || defaultFormatDate;

  // ---------- State ----------
  const valueIsDate = isDateString(value);
  const initialMode = mode === "status" && valueIsDate ? "date" : mode;
  const [editMode, setEditMode] = useState(initialMode);
  const [temp, setTemp] = useState(
    editMode === "date" ? format(parseDate(value)) : value || ""
  );
  const [openPicker, setOpenPicker] = useState(false);
  const [prevTemp, setPrevTemp] = useState(null);
  const [prevEditMode, setPrevEditMode] = useState(null);

  // ---------- Save ----------
  const saveChange = async (newVal, currentMode = editMode) => {
    try {
      await onSave?.(newVal, currentMode);
      setPrevTemp(null);
      setPrevEditMode(null);
    } catch (err) {
      console.error("EditableInput save error:", err);
    }
  };

  // ---------- Handlers ----------
  const openPickerFromStatus = () => {
    setPrevTemp(temp);
    setPrevEditMode(editMode);
    setEditMode("date");
    setOpenPicker(true);
  };

  const closePickerHandler = () => {
    setOpenPicker(false);
    if (prevEditMode === "status") {
      setTemp(prevTemp ?? "");
      setEditMode("status");
      setPrevTemp(null);
      setPrevEditMode(null);
    } else {
      setPrevTemp(null);
      setPrevEditMode(null);
    }
  };

  const handleStatusBlur = () => {
    setTimeout(() => {
      if (!openPicker) {
        saveChange(temp, "status");
        if (isDateString(temp)) setEditMode("date");
      }
    }, 180);
  };

  // ---------- Render ----------
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {editMode === "status" || editMode === "text" ? (
        <input
          value={temp}
          onChange={(e) => setTemp(e.target.value)}
          onBlur={handleStatusBlur}
          onClick={() => {
            if (editMode === "status" && !valueIsDate) {
              openPickerFromStatus();
            }
          }}
          placeholder={placeholder}
          readOnly={readOnly}
          className="editable-input"
        />
      ) : (
        <div
          onDoubleClick={() => {
            setPrevTemp(temp);
            setPrevEditMode(editMode);
            setOpenPicker(true);
          }}
        >
          <DatePicker
            selected={parseDate(temp)}
            open={openPicker}
            onChange={(date) => {
              const newVal = format(date);
              setTemp(newVal);
              setOpenPicker(false);
              setEditMode("date");
              saveChange(newVal, "date");
            }}
            onClickOutside={closePickerHandler}
            placeholderText={placeholder}
            dateFormat="dd-MM-yyyy"
            isClearable={!(temp && temp.length > 0)}
            popperPlacement="bottom-start"
            className="editable-input"
          />
        </div>
      )}
    </div>
  );
}
